import React from 'react'
import PropTypes from 'prop-types'
import moment from 'moment-timezone'
import { withTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { formValueSelector } from 'redux-form'

import { Dropdown } from 'ggx-componentlibrary/components/dropdown/dropdown.component'
import { Datepicker } from 'ggx-componentlibrary/components/datepicker/datepicker.component'
import { StyledError } from 'ggx-componentlibrary/components/text-field/text-field.component.styles'
import { getMomentTimeZone } from '../../../../../service/locale/timezone'
import { PICKUP_TIME_OPTIONS } from './pickup-time.constants'
import { PickupTimeGroup } from './pickup-time.component.styles'
import {
  StyledWrapper,
  StyledSubheading,
} from '../../order-form.component.styles'

const DELIVERY_FORM_NAME = 'delivery-order'

const currentTimeZone = getMomentTimeZone()

class PickupTime extends React.Component {
  static titleId = 'delivery-pickup-time-heading'

  static propTypes = {
    input: PropTypes.object.isRequired,
    meta: PropTypes.object.isRequired,
    currentISOTime: PropTypes.string.isRequired,
    t: PropTypes.func,
    i18n: PropTypes.object,
    deliveryType: PropTypes.string,
  }

  state = {
    selectedDate: null,
    selectedTime: null,
  }

  selectableTimes = null

  handleDateSelect = option => {
    this.setState({ selectedDate: option })
    this._handlePickUpTimeSelect({ date: (option && option.value) || null })
  }

  handleTimeSelect = option => {
    this.setState({ selectedTime: option })
    this._handlePickUpTimeSelect({ time: (option && option.value) || null })
  }

  /**
   * We rely on redux-form to provide us the value we've emitted through onChange, but
   * currently do not support changing the pickup time selection via the value prop
   */
  handleBlur = () => {
    const { onBlur, value } = this.props.input

    onBlur(value)
  }

  _handlePickUpTimeSelect = ({ date, time }) => {
    const { onChange } = this.props.input

    const selectedDate =
      date === null ? date : date || (this.state.selectedDate || {}).value
    const selectedTime =
      time === null ? time : time || (this.state.selectedTime || {}).value

    if (selectedTime === PICKUP_TIME_OPTIONS.NOW || selectedTime === null) {
      return onChange(selectedTime)
    }

    const selectedDateMoment = moment.tz(
      selectedDate,
      moment.ISO_8601,
      currentTimeZone
    )
    onChange(selectedDateMoment.set(selectedTime).format())
  }

  _getCurrentTime() {
    const { currentISOTime } = this.props

    return moment.tz(currentISOTime, moment.ISO_8601, currentTimeZone)
  }

  _createSelectableTimes() {
    const { t, i18n } = this.props
    const earliestTime = this._getCurrentTime().set({ h: 8, m: 0, s: 0, ms: 0 })
    const latestTime = earliestTime.clone().set({ h: 21, m: 50 })
    const nowOption = {
      label: t('orders.new.basicInfo.selection.now'),
      value: PICKUP_TIME_OPTIONS.NOW,
    }
    const selectableTimes = [nowOption]
    const selectableTime = earliestTime.clone()
    while (selectableTime.isSameOrBefore(latestTime)) {
      const selectableOption = {
        label: selectableTime
          .locale(i18n.language)
          .format(t('moment.formatShort')),
        value: { hour: selectableTime.hour(), minute: selectableTime.minute() },
      }
      selectableTimes.push(selectableOption)
      selectableTime.add({ minute: 10 })
    }

    return selectableTimes
  }

  _getSelectableTimes({ after, showNow }) {
    this.selectableTimes = this._createSelectableTimes()

    const displayAfter = moment.isMoment(after) && {
      hour: after.get('hour'),
      minute: after.get('minute'),
    }
    const filterPastTimes = ({ value }) =>
      typeof value === 'string' ||
      value.hour > displayAfter.hour ||
      (value.hour === displayAfter.hour && value.minute > displayAfter.minute)

    const filteredNow = showNow
      ? this.selectableTimes
      : this.selectableTimes.slice(1)
    const filteredTimes = displayAfter
      ? filteredNow.filter(filterPastTimes)
      : filteredNow

    return filteredTimes
  }

  /**
   * Day-picker: Show Today + tomorrow + 5 next days
   * 12am-8am: Today - 8am - 7:50pm
   * 8am-8pm:  Today - Now - 7:50pm
   * 8pm-12am: Hide Today
   * @see https://gogotech.atlassian.net/browse/DT-634
   * @returns {{
   *  selectableTimes: { label: string, value: string }[],
   *  defaultTime: { label: string, value: string },
   *  showToday: boolean,
   *  selectedDateIsInvalid: boolean,
   *  selectedTimeIsInvalid: boolean,
   * }}
   */
  _getDisplayRules() {
    const { deliveryType } = this.props
    const selectedDate = (this.state.selectedDate || {}).value
    const selectedTime = this.state.selectedTime || {}
    const currentTime = this._getCurrentTime()
    const morningCutoff = currentTime.clone().set({ h: 8, m: 0, s: 0, ms: 0 })
    const nightCutoff =
      deliveryType === 'instant'
        ? currentTime.clone().set({ h: 22, m: 0, s: 0, ms: 0 })
        : currentTime.clone().set({ h: 19, m: 50, s: 0, ms: 0 })
    const selectedDateIsInFuture = moment
      .tz(selectedDate, moment.ISO_8601, currentTimeZone)
      .isAfter(currentTime, 'day')
    const selectableTimeUpperLimit = selectedDateIsInFuture ? null : currentTime
    const showNow =
      !selectedDateIsInFuture && currentTime.isSameOrAfter(morningCutoff)

    const selectableTimes = this._getSelectableTimes({
      after: selectableTimeUpperLimit,
      showNow,
    })
    const defaultTime = selectableTimes[0]
    const showToday = currentTime.isBefore(nightCutoff)
    const selectedDateIsInvalid = !selectedDateIsInFuture && !showToday
    const selectedTimeIsInvalid = !selectableTimes.some(
      option => option.label === selectedTime.label
    )

    return {
      defaultTime,
      selectableTimes,
      showToday,
      selectedDateIsInvalid,
      selectedTimeIsInvalid,
      currentTime: currentTime.format(),
    }
  }

  _handleTimePreselection(time) {
    setTimeout(() => this.handleTimeSelect(time))
  }

  _handleInvalidSelection() {
    setTimeout(() => this._handlePickUpTimeSelect({ time: null }))
  }

  render() {
    const { meta, t, i18n } = this.props
    const { selectedDate, selectedTime } = this.state
    const {
      selectableTimes,
      defaultTime,
      showToday,
      selectedDateIsInvalid,
      selectedTimeIsInvalid,
      currentTime,
    } = this._getDisplayRules()

    if (selectedDateIsInvalid) {
      this._handleInvalidSelection()
    } else if (!selectedTime || selectedTimeIsInvalid) {
      this._handleTimePreselection(defaultTime)
    }

    return (
      <StyledWrapper>
        <StyledSubheading id={PickupTime.titleId}>
          {t('orders.new.basicInfo.subtitle.pickupTime')}
        </StyledSubheading>
        <PickupTimeGroup>
          <Datepicker
            dataTestid="order-page-pickup-date"
            numberOfDays={5}
            showToday={showToday}
            currentDay={currentTime}
            value={selectedDate}
            onChange={this.handleDateSelect}
            i18n={{
              today: t('orders.new.basicInfo.selection.today'),
              tomorrow: t('orders.new.basicInfo.selection.tomorrow'),
              dateFormat: t('moment.formatDateOnly'),
              timezone: currentTimeZone,
              locale: i18n.language,
            }}
            onBlur={this.handleBlur}
          />
          <Dropdown
            dataTestid="order-page-pickup-time"
            defaultOptions={selectableTimes}
            value={selectedTime || defaultTime}
            onChange={this.handleTimeSelect}
            maxHeight={300}
            onBlur={this.handleBlur}
          />
        </PickupTimeGroup>
        {meta.error && meta.touched ? (
          <StyledError>{meta.error}</StyledError>
        ) : null}
      </StyledWrapper>
    )
  }
}

const PickupTimeWithTranslation = withTranslation()(PickupTime)

const selector = formValueSelector(DELIVERY_FORM_NAME)

const mapStateToProps = state => ({
  deliveryType: selector(state, 'delivery_quotation_type'),
})

const ConnectedPickupTimeWithTranslation = connect(mapStateToProps)(
  PickupTimeWithTranslation
)

export { PickupTimeWithTranslation, ConnectedPickupTimeWithTranslation }
